/**
 * Persona Studio Library page
 * Displays all personas in a grid with CRUD actions
 */

import { useState } from 'react';
import { useNavigate } from 'react-router';
import { Button, Card, Icon, Modal, toast } from '@lib/ui';
import { usePersonas, useDeletePersona } from './hooks/usePersonaApi';
import { PersonaCard } from './components/PersonaCard';
import type { Persona } from './types';

export default function PersonaStudioLibrary() {
  const navigate = useNavigate();
  const { personas, isLoading, error, refetch } = usePersonas();
  const { deletePersona, isDeleting } = useDeletePersona();

  const [deleteTarget, setDeleteTarget] = useState<Persona | null>(null);

  const handleEdit = (persona: Persona) => {
    navigate(`/persona-studio/${persona.id}/edit`);
  };

  const handleDelete = (persona: Persona) => {
    setDeleteTarget(persona);
  };

  const handleTest = (persona: Persona) => {
    navigate(`/persona-studio/playground?persona=${persona.id}`);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;

    try {
      await deletePersona(deleteTarget.id);
      toast.success(`"${deleteTarget.name}" deleted successfully`);
      setDeleteTarget(null);
      refetch();
    } catch {
      toast.error('Failed to delete persona');
    }
  };

  return (
    <div className="p-6 space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">Persona Studio</h1>
          <p className="mt-1 text-gray-500 dark:text-dark-300">
            Create and manage your content personas
          </p>
        </div>
        <Button color="primary" onClick={() => navigate('/persona-studio/create')}>
          <Icon name="plus" size="md" />
          <span className="ml-2">Create Persona</span>
        </Button>
      </div>

      {/* Error state */}
      {error && (
        <Card className="p-4 bg-error-50 dark:bg-error-900/20 border border-error-200 dark:border-error-800">
          <div className="flex items-center gap-3">
            <Icon name="error" size="md" className="text-error-500" />
            <p className="text-sm text-error-700 dark:text-error-300">{error}</p>
            <Button variant="flat" className="ml-auto" onClick={refetch}>
              Retry
            </Button>
          </div>
        </Card>
      )}

      {/* Loading state */}
      {isLoading && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1, 2, 3].map((i) => (
            <Card key={i} skin="bordered" className="animate-pulse">
              <div className="p-4 border-b border-gray-200 dark:border-dark-600">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-full bg-gray-200 dark:bg-dark-600" />
                  <div className="flex-1 space-y-2">
                    <div className="h-4 bg-gray-200 dark:bg-dark-600 rounded w-3/4" />
                    <div className="h-3 bg-gray-200 dark:bg-dark-600 rounded w-1/2" />
                  </div>
                </div>
              </div>
              <div className="p-4 space-y-4">
                <div className="space-y-2">
                  <div className="h-3 bg-gray-200 dark:bg-dark-600 rounded w-1/4" />
                  <div className="flex gap-1">
                    <div className="h-5 bg-gray-200 dark:bg-dark-600 rounded w-16" />
                    <div className="h-5 bg-gray-200 dark:bg-dark-600 rounded w-16" />
                  </div>
                </div>
              </div>
              <div className="px-4 py-3 bg-gray-50 dark:bg-dark-700 border-t border-gray-200 dark:border-dark-600">
                <div className="h-8 bg-gray-200 dark:bg-dark-600 rounded" />
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Empty state */}
      {!isLoading && !error && personas.length === 0 && (
        <Card
          skin="bordered"
          className="p-12 text-center border-2 border-dashed border-gray-300 dark:border-dark-500"
        >
          <div className="max-w-md mx-auto">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center">
              <Icon name="users" size="xl" className="text-primary-500" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
              No personas yet
            </h3>
            <p className="text-gray-500 dark:text-dark-400 mb-6">
              Create your first persona to start generating content with a consistent voice.
            </p>
            <Button color="primary" onClick={() => navigate('/persona-studio/create')}>
              <Icon name="plus" size="md" />
              <span className="ml-2">Create Your First Persona</span>
            </Button>
          </div>
        </Card>
      )}

      {/* Personas grid */}
      {!isLoading && !error && personas.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {personas.map((persona) => (
            <PersonaCard
              key={persona.id}
              persona={persona}
              onEdit={handleEdit}
              onDelete={handleDelete}
              onTest={handleTest}
            />
          ))}
        </div>
      )}

      {/* Delete confirmation modal */}
      <Modal
        isOpen={deleteTarget !== null}
        onClose={() => setDeleteTarget(null)}
        title="Delete Persona"
      >
        <div className="p-4">
          <p className="text-gray-600 dark:text-dark-300">
            Are you sure you want to delete <strong>"{deleteTarget?.name}"</strong>? This action
            cannot be undone.
          </p>
        </div>
        <div className="flex justify-end gap-3 px-4 py-3 bg-gray-50 dark:bg-dark-700 border-t border-gray-200 dark:border-dark-600">
          <Button variant="flat" onClick={() => setDeleteTarget(null)}>
            Cancel
          </Button>
          <Button color="error" onClick={confirmDelete} disabled={isDeleting}>
            {isDeleting ? 'Deleting...' : 'Delete'}
          </Button>
        </div>
      </Modal>
    </div>
  );
}
